import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import Topbar from "../components/topbar";
import { useToast } from "../providers/toaster-provider";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import SectionWrapper from "../components/section";
import { XeroConnectedView, XeroConnectView } from "../components/xero";
import { StripeConnectedView, StripeConnectView } from "../components/stripe";
import { useInvoiceHorizonCheck } from "../components/popup/xero-invoice-horizon";
import { usePlanFeature } from "../hooks/use-plan-feature";
import { Box } from "@mui/material";
import PlanUpgradeBar from "../components/ui/plan-upgrade-bar";

type XeroStatus = 'Pending' | 'Active' | 'Disconnected';

interface StripeSettings {
    configured: boolean;
    isEnabled?: boolean;
    publishableKey?: string;
    lastSync?: { at: string; summary: string } | null;
}

interface XeroIntegration {
    status: XeroStatus;
    tenantName?: string;
    bankAccountId?: number | null;
    xeroBankAccountId?: string | null;
}

// Generic fallback - used only when no active sync type is known.
const isTerminalLog = (description: string) =>
    /import complete|sync failed|sync timedout|polling timed out|cancelled/i.test(description);

// Invoice sync now runs a client-sync sub-step first (see startInvoiceImport,
// xero-import-service.js), so the activity log polled during an "invoices"
// sync can legitimately show a "Client import complete..." line before the
// invoice import itself has even started. That line also matches the
// generic isTerminalLog pattern above (it contains "import complete"), which
// would stop the poll and re-enable the button while the real invoice
// import is still running in the background. Scoping the terminal check to
// the exact completion/failure text each sync type actually produces (see
// the matching `log(...)` calls in xero-import-service.js) avoids that -
// only that type's own step ends its own polling session.
const TERMINAL_PATTERNS: Record<string, RegExp> = {
    gl: /gl accounts import (complete|failed)|no gl accounts returned|gl sync polling timed out|gl sync \w+ in xero-sync|failed to fetch gl accounts/i,
    transactions: /bank transaction import (complete|failed)|no bank transactions returned|banktx sync polling timed out|banktx sync \w+ in xero-sync|failed to fetch bank transactions/i,
    invoices: /invoice import (complete|failed)|no invoices returned|invoices sync polling timed out|invoices sync \w+ in xero-sync|failed to fetch invoices/i,
    // Status sync now waits for a live Xero pull first (startLiveInvoiceStatusSync),
    // so that pull's own polling failure/timeout lines end it too.
    statuses: /invoice status sync complete|no invoice statuses returned|failed to fetch invoice statuses|statuses sync polling timed out|statuses sync \w+ in xero-sync/i,
    clients: /client import complete|no contacts returned|failed to fetch contacts/i,
};

const isTerminalLogForType = (description: string, activeType: string | null) => {
    const pattern = activeType ? TERMINAL_PATTERNS[activeType] : null;
    return pattern ? pattern.test(description) : isTerminalLog(description);
};

const Integrations: React.FC = () => {
    const { checkInvoiceHorizon, invoiceHorizonDialog } = useInvoiceHorizonCheck();
    // Without the plan feature a section is read-only: connecting, syncing and
    // saving keys open the upgrade / add-on prompt (disconnecting still works).
    const { has: hasXero, prompt: promptXero } = usePlanFeature('xeroIntegration');
    const { has: hasStripe, prompt: promptStripe } = usePlanFeature('stripeIntegration');
    const { user } = useUser();
    const { can } = usePermissions();
    const { showSuccess, showError } = useToast();
    const entityId = user?.entityId;


    const [integration, setIntegration] = useState<XeroIntegration | null>(null);
    const [lastGLSyncAt, setLastGLSyncAt] = useState<string | null>(null);
    const [lastTransactionSyncAt, setLastTransactionSyncAt] = useState<string | null>(null);
    const [lastStatusesSyncAt, setLastStatusesSyncAt] = useState<string | null>(null);
    const [lastInvoicesSyncAt, setLastInvoicesSyncAt] = useState<string | null>(null);
    const [lastClientsSyncAt, setLastClientsSyncAt] = useState<string | null>(null);
    const [importStatusText, setImportStatusText] = useState<string | null>(null);
    // Result of each sync type's last import attempt (status + the human-readable
    // detail message, e.g. "Invoice import complete - 92 invoices..." or a timeout
    // failure) - getSyncHistory already computes this server-side, just wasn't
    // surfaced in the UI before.
    const [syncResults, setSyncResults] = useState<Record<string, { status: string | null; detail: string | null }>>({});

    const [bankAccounts, setBankAccounts] = useState<{ id: number; name: string }[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | null>(null);
    const [xeroBankAccounts, setXeroBankAccounts] = useState<{ id: string; name: string }[]>([]);
    const [selectedXeroBankAccountId, setSelectedXeroBankAccountId] = useState<string | null>(null);
    const [fromDate, setFromDate] = useState('');

    const [connecting, setConnecting] = useState(false);
    const [syncingGL, setSyncingGL] = useState(false);
    const [syncingTransactions, setSyncingTransactions] = useState(false);
    const [syncingInvoices, setSyncingInvoices] = useState(false);
    const [syncingStatuses, setSyncingStatuses] = useState(false);
    const [syncingClients, setSyncingClients] = useState(false);
    const [statusesSynced, setStatusesSynced] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const syncGLPendingRef = useRef(false);
    const syncTxPendingRef = useRef(false);
    const syncStatusesPendingRef = useRef(false);
    const syncInvoicesPendingRef = useRef(false);
    const syncClientsPendingRef = useRef(false);
    // Which sync the current import-status poll is following - lets the
    // shared terminal-log handler below know which syncing-flag to clear.
    const activeSyncTypeRef = useRef<'gl' | 'transactions' | 'statuses' | 'invoices' | 'clients' | null>(null);
    const importPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const importPollSettledRef = useRef(false);
    const entityIdRef = useRef(entityId);

    const [getResult, doGet] = useApi<any>();
    const [connectResult, doConnect] = useApi<any>();
    const [disconnectResult, doDisconnect] = useApi<any>();
    const [syncGLResult, doSyncGL] = useApi<any>();
    const [syncTxResult, doSyncTx] = useApi<any>();
    const [syncInvoicesResult, doSyncInvoices] = useApi<any>();
    const [syncStatusesResult, doSyncStatuses] = useApi<any>();
    const [syncClientsResult, doSyncClients] = useApi<any>();
    const [syncHistoryResult, doGetSyncHistory] = useApi<any>();
    const [importStatusResult, doGetImportStatus] = useApi<any>();
    const [bankAccountsResult, doGetBankAccounts] = useApi<any>();
    const [glAccountsResult, doGetGLAccounts] = useApi<any>();

    // Stripe state
    const [stripe, setStripe] = useState<StripeSettings | null>(null);
    const [stripePk, setStripePk] = useState('');
    const [stripeSk, setStripeSk] = useState('');
    const [stripeEditing, setStripeEditing] = useState(false);
    const [stripeSaving, setStripeSaving] = useState(false);
    const [stripeDisconnecting, setStripeDisconnecting] = useState(false);
    const [stripeGetResult, doGetStripe] = useApi<any>();
    const [stripeSaveResult, doSaveStripe] = useApi<any>();
    const [stripeDeleteResult, doDeleteStripe] = useApi<any>();
    const [stripeSyncResult, doSyncStripe] = useApi<any>();
    const [stripeSyncStatusResult, doGetStripeSyncStatus] = useApi<any>();
    const [stripeSyncing, setStripeSyncing] = useState(false);
    const [stripeSyncText, setStripeSyncText] = useState<string | null>(null);
    const stripeSyncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Deep-link target for the "connect Stripe" note on the invoice share
    // popup (invoice-share-permissions.tsx) - lets that link land here
    // already scrolled to the right section instead of just the page top.
    const stripeSectionRef = useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        if (searchParams.get('section') !== 'stripe') return;
        stripeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [searchParams]);

    useEffect(() => { entityIdRef.current = entityId; }, [entityId]);

    const fetchIntegration = useCallback(() => {
        if (!entityIdRef.current) return;
        doGet(buildApiRequestConfig(routes.xero.getIntegration, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGet]);

    const fetchSyncHistory = useCallback(() => {
        if (!entityIdRef.current) return;
        doGetSyncHistory(buildApiRequestConfig(routes.xero.syncHistory, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGetSyncHistory]);

    const fetchBankAccounts = useCallback(() => {
        if (!entityIdRef.current) return;
        doGetBankAccounts(buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGetBankAccounts]);

    // Xero's own bank accounts (from GL sync, which pulls the full chart of
    // accounts including BANK-type ones) - the "source" side of the sync, as
    // opposed to bankAccounts above which is our own destination account.
    const fetchXeroBankAccounts = useCallback(() => {
        if (!entityIdRef.current) return;
        doGetGLAccounts(buildApiRequestConfig(routes.xero.getGLAccounts, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGetGLAccounts]);

    const fetchImportStatus = useCallback(() => {
        if (!entityIdRef.current) return;
        doGetImportStatus(buildApiRequestConfig(routes.xero.importStatus, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGetImportStatus]);

    const startImportPolling = useCallback(() => {
        if (importPollRef.current) clearInterval(importPollRef.current);
        setImportStatusText(null);
        importPollRef.current = setInterval(fetchImportStatus, 3000);
    }, [fetchImportStatus]);

    useEffect(() => {
        fetchIntegration();
        if (entityId) {
            doGetStripe(buildApiRequestConfig(routes.stripe.get, [{ param: 'entityId', value: entityId }]));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchIntegration]);

    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (importPollRef.current) clearInterval(importPollRef.current);
            if (stripeSyncPollRef.current) clearInterval(stripeSyncPollRef.current);
        };
    }, []);

    // Integration fetch result
    useEffect(() => {
        if (getResult.data) {
            const payload = getResult.data.data ?? getResult.data;
            const record: XeroIntegration = payload.integration ?? payload;
            setIntegration(record);
            // Prefill from the last-used mapping (saved server-side on the
            // last Sync Transactions click) - doesn't override a choice
            // already made this session.
            if (record.bankAccountId != null) {
                setSelectedBankAccountId(prev => prev ?? record.bankAccountId ?? null);
            }
            if (record.xeroBankAccountId != null) {
                setSelectedXeroBankAccountId(prev => prev ?? record.xeroBankAccountId ?? null);
            }
            if (record.status === 'Pending' && !pollRef.current) {
                pollRef.current = setInterval(fetchIntegration, 3000);
            }
            if (record.status === 'Active') {
                fetchSyncHistory();
                fetchBankAccounts();
                fetchXeroBankAccounts();
            }
        }
    }, [getResult.data, fetchIntegration, fetchSyncHistory, fetchBankAccounts, fetchXeroBankAccounts]);

    // Stop connection polling once Active
    useEffect(() => {
        if (integration?.status === 'Active' && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, [integration?.status]);

    // Sync history result
    useEffect(() => {
        if (syncHistoryResult.data) {
            const payload = syncHistoryResult.data.data ?? syncHistoryResult.data;
            setLastGLSyncAt(payload.lastGLSync?.triggeredAt ?? null);
            setLastTransactionSyncAt(payload.lastTransactionSync?.triggeredAt ?? null);
            setLastStatusesSyncAt(payload.lastStatusesSync?.triggeredAt ?? null);
            setLastInvoicesSyncAt(payload.lastInvoicesSync?.triggeredAt ?? null);
            setLastClientsSyncAt(payload.lastClientsSync?.triggeredAt ?? null);
            setSyncResults({
                gl: { status: payload.lastGLSync?.status ?? null, detail: payload.lastGLSync?.detail ?? null },
                transactions: { status: payload.lastTransactionSync?.status ?? null, detail: payload.lastTransactionSync?.detail ?? null },
                statuses: { status: payload.lastStatusesSync?.status ?? null, detail: payload.lastStatusesSync?.detail ?? null },
                invoices: { status: payload.lastInvoicesSync?.status ?? null, detail: payload.lastInvoicesSync?.detail ?? null },
                clients: { status: payload.lastClientsSync?.status ?? null, detail: payload.lastClientsSync?.detail ?? null },
            });
            // statusesSynced gates the Sync Invoices button - it's meant to reflect
            // whether statuses have ever been synced, not just whether this browser
            // session happened to watch one complete. Without this, reloading the
            // page (or coming back later) permanently re-locks Sync Invoices even
            // though the server already has a real lastStatusesSync timestamp.
            if (payload.lastStatusesSync?.triggeredAt) setStatusesSynced(true);

            const glInProgress = payload.lastGLSync?.status === 'in_progress';
            const txInProgress = payload.lastTransactionSync?.status === 'in_progress';
            if ((glInProgress || txInProgress) && !importPollRef.current && !importPollSettledRef.current) {
                if (glInProgress) setSyncingGL(true);
                if (txInProgress) setSyncingTransactions(true);
                startImportPolling();
            }
        }
    }, [syncHistoryResult.data, startImportPolling]);

    // Import status polling result
    useEffect(() => {
        if (!importStatusResult.data) return;
        const logs: any[] = importStatusResult.data.data ?? importStatusResult.data ?? [];
        if (logs.length === 0) return;
        const latest = logs[0];
        const description: string = latest.description ?? '';
        setImportStatusText(description);
        if (isTerminalLogForType(description, activeSyncTypeRef.current)) {
            importPollSettledRef.current = true;
            if (importPollRef.current) {
                clearInterval(importPollRef.current);
                importPollRef.current = null;
            }
            const failed = /failed|timed ?out|cancelled/i.test(description);
            switch (activeSyncTypeRef.current) {
                case 'statuses':
                    setSyncingStatuses(false);
                    if (!failed) setStatusesSynced(true);
                    break;
                case 'invoices':
                    setSyncingInvoices(false);
                    // Invoices held back past the projection horizon.
                    if (!failed) checkInvoiceHorizon();
                    break;
                case 'clients':
                    setSyncingClients(false);
                    break;
                case 'transactions':
                    setSyncingTransactions(false);
                    break;
                case 'gl':
                    setSyncingGL(false);
                    // Xero bank accounts (the sync "source" dropdown) come from
                    // the GL account list - refresh it now that a fresh one exists.
                    if (!failed) fetchXeroBankAccounts();
                    break;
                default:
                    setSyncingGL(false);
                    setSyncingTransactions(false);
            }
            activeSyncTypeRef.current = null;
            fetchSyncHistory();
        }
    }, [importStatusResult.data, fetchSyncHistory, fetchXeroBankAccounts, checkInvoiceHorizon]);

    // Connect result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!connectResult.data && !connectResult.error) return;
        if (connectResult.data) {
            const payload = connectResult.data.data ?? connectResult.data;
            const { connectUrl, integration: record } = payload;
            if (record) setIntegration(record);
            if (connectUrl) {
                window.open(connectUrl, '_blank');
                if (pollRef.current) clearInterval(pollRef.current);
                pollRef.current = setInterval(fetchIntegration, 3000);
            }
        }
        if (connectResult.error) {
            const errData = connectResult.errorData;
            // A plan/add-on gap opens the app-wide upgrade prompt
            // (components/plan-required-gate.tsx) - nothing more to show here.
            if (errData?.action === 'upgrade_required' || errData?.action === 'addon_required') {
                // handled app-wide
            } else {
                showError(typeof connectResult.error === 'string' ? connectResult.error : 'Failed to connect Xero');
            }
        }
        setConnecting(false);
    }, [connectResult.data, connectResult.error]);

    // Disconnect result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!disconnectResult.data && !disconnectResult.error) return;
        if (disconnectResult.data) {
            setIntegration(null);
            setLastGLSyncAt(null);
            setLastTransactionSyncAt(null);
            setImportStatusText(null);
            showSuccess('Xero disconnected');
        }
        if (disconnectResult.error) {
            showError(typeof disconnectResult.error === 'string' ? disconnectResult.error : 'Failed to disconnect Xero');
        }
    }, [disconnectResult.data, disconnectResult.error]);

    // Bank accounts result
    useEffect(() => {
        if (!bankAccountsResult.data) return;
        const payload = bankAccountsResult.data.data ?? bankAccountsResult.data;
        const accounts = Array.isArray(payload) ? payload : (payload.bankAccounts ?? []);
        const mapped = accounts.map((a: any) => ({ id: a.id, name: a.name }));
        setBankAccounts(mapped);
        // Only one account to choose from - pick it automatically instead of
        // making the user select it every time before Sync Transactions/
        // Invoices unlock. Doesn't override a choice already made (e.g. from
        // a prior fetch in this session).
        if (mapped.length === 1) {
            setSelectedBankAccountId(prev => prev ?? mapped[0].id);
        }
    }, [bankAccountsResult.data]);

    // Xero bank accounts result (GL accounts filtered to BANK type)
    useEffect(() => {
        if (!glAccountsResult.data) return;
        const payload = glAccountsResult.data.data ?? glAccountsResult.data;
        const accounts = Array.isArray(payload) ? payload : [];
        const mapped = accounts
            .filter((a: any) => a.type === 'BANK')
            .map((a: any) => ({ id: a.account_id, name: a.name }));
        setXeroBankAccounts(mapped);
        if (mapped.length === 1) {
            setSelectedXeroBankAccountId(prev => prev ?? mapped[0].id);
        }
    }, [glAccountsResult.data]);

    // GL sync result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!syncGLPendingRef.current) return;
        if (syncGLResult.isLoading) return;
        syncGLPendingRef.current = false;
        if (syncGLResult.error) {
            showError(typeof syncGLResult.error === 'string' ? syncGLResult.error : 'Failed to trigger GL sync');
            setSyncingGL(false);
        } else {
            startImportPolling();
        }
    }, [syncGLResult.data, syncGLResult.error, syncGLResult.isLoading]);

    // Transaction sync result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!syncTxPendingRef.current) return;
        if (syncTxResult.isLoading) return;
        syncTxPendingRef.current = false;
        if (syncTxResult.error) {
            showError(typeof syncTxResult.error === 'string' ? syncTxResult.error : 'Failed to trigger transaction sync');
            setSyncingTransactions(false);
        } else {
            startImportPolling();
        }
    }, [syncTxResult.data, syncTxResult.error, syncTxResult.isLoading]);

    // Statuses sync result - only the immediate trigger ack; the real
    // outcome (success/failure/counts) comes from polling ActivityLog below,
    // same as GL/Transactions, instead of a generic "synced" toast that
    // can't tell you what actually happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!syncStatusesPendingRef.current) return;
        if (syncStatusesResult.isLoading) return;
        syncStatusesPendingRef.current = false;
        if (syncStatusesResult.error) {
            showError(typeof syncStatusesResult.error === 'string' ? syncStatusesResult.error : 'Failed to trigger status sync');
            setSyncingStatuses(false);
            activeSyncTypeRef.current = null;
        } else {
            startImportPolling();
        }
    }, [syncStatusesResult.data, syncStatusesResult.error, syncStatusesResult.isLoading]);

    // Invoices sync result - see Statuses comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!syncInvoicesPendingRef.current) return;
        if (syncInvoicesResult.isLoading) return;
        syncInvoicesPendingRef.current = false;
        if (syncInvoicesResult.error) {
            showError(typeof syncInvoicesResult.error === 'string' ? syncInvoicesResult.error : 'Failed to trigger invoice sync');
            setSyncingInvoices(false);
            activeSyncTypeRef.current = null;
        } else {
            startImportPolling();
        }
    }, [syncInvoicesResult.data, syncInvoicesResult.error, syncInvoicesResult.isLoading]);

    // Clients sync result - see Statuses comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!syncClientsPendingRef.current) return;
        if (syncClientsResult.isLoading) return;
        syncClientsPendingRef.current = false;
        if (syncClientsResult.error) {
            showError(typeof syncClientsResult.error === 'string' ? syncClientsResult.error : 'Failed to trigger client sync');
            setSyncingClients(false);
            activeSyncTypeRef.current = null;
        } else {
            startImportPolling();
        }
    }, [syncClientsResult.data, syncClientsResult.error, syncClientsResult.isLoading]);

    const handleConnect = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId) return;
        setConnecting(true);
        // An integration row already exists once you've connected before
        // (disconnect only marks it 'Disconnected', it doesn't delete the
        // row) - POST /xero/connect 409s in that case since it's create-only.
        // refreshConnectLink reuses the same xero-sync tenant record for a
        // fresh authorize URL instead, preserving sync history.
        if (integration) {
            doConnect(buildApiRequestConfig(routes.xero.refreshConnectLink, [{ param: 'entityId', value: entityId }]));
        } else {
            doConnect(buildApiRequestConfig(routes.xero.connect, [{ param: 'entityId', value: entityId }]));
        }
    };

    const handleDisconnect = () => {
        if (!entityId) return;
        doDisconnect(buildApiRequestConfig(routes.xero.disconnect, [{ param: 'entityId', value: entityId }]));
    };

    const handleSyncGL = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId) return;
        setSyncingGL(true);
        syncGLPendingRef.current = true;
        activeSyncTypeRef.current = 'gl';
        importPollSettledRef.current = false;
        doSyncGL(buildApiRequestConfig(routes.xero.syncGL, [{ param: 'entityId', value: entityId }]));
    };

    const handleSyncTransactions = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId || !selectedBankAccountId || !selectedXeroBankAccountId) return;
        setSyncingTransactions(true);
        syncTxPendingRef.current = true;
        activeSyncTypeRef.current = 'transactions';
        importPollSettledRef.current = false;
        doSyncTx({
            ...buildApiRequestConfig(routes.xero.syncTransactions, [
                { param: 'entityId', value: entityId },
                { param: 'fromDate', value: fromDate || null },
            ]),
            body: { bankAccountId: selectedBankAccountId, xeroBankAccountId: selectedXeroBankAccountId },
        });
    };

    const handleSyncStatuses = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId) return;
        setSyncingStatuses(true);
        syncStatusesPendingRef.current = true;
        activeSyncTypeRef.current = 'statuses';
        importPollSettledRef.current = false;
        doSyncStatuses(buildApiRequestConfig(routes.xero.syncStatuses, [{ param: 'entityId', value: entityId }]));
    };

    const handleSyncInvoices = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId || !selectedBankAccountId) return;
        setSyncingInvoices(true);
        syncInvoicesPendingRef.current = true;
        activeSyncTypeRef.current = 'invoices';
        importPollSettledRef.current = false;
        doSyncInvoices({
            ...buildApiRequestConfig(routes.xero.syncInvoices, [
                { param: 'entityId', value: entityId },
                { param: 'fromDate', value: fromDate || null },
            ]),
            body: { bankAccountId: selectedBankAccountId },
        });
    };

    const handleSyncClients = () => {
        if (!hasXero) { promptXero(); return; }
        if (!entityId) return;
        setSyncingClients(true);
        syncClientsPendingRef.current = true;
        activeSyncTypeRef.current = 'clients';
        importPollSettledRef.current = false;
        doSyncClients(buildApiRequestConfig(routes.xero.syncClients, [{ param: 'entityId', value: entityId }]));
    };

    // Stripe fetch result
    useEffect(() => {
        if (!stripeGetResult.data) return;
        const payload = stripeGetResult.data.data ?? stripeGetResult.data;
        setStripe(payload);
    }, [stripeGetResult.data]);

    // Stripe save result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!stripeSaveResult.data && !stripeSaveResult.error) return;
        if (stripeSaveResult.data) {
            const payload = stripeSaveResult.data.data ?? stripeSaveResult.data;
            setStripe(payload);
            setStripePk('');
            setStripeSk('');
            setStripeEditing(false);
            showSuccess('Stripe keys saved');
        }
        if (stripeSaveResult.error) showError(typeof stripeSaveResult.error === 'string' ? stripeSaveResult.error : 'Failed to save Stripe keys');
        setStripeSaving(false);
    }, [stripeSaveResult.data, stripeSaveResult.error]);

    // Stripe delete result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!stripeDeleteResult.data && !stripeDeleteResult.error) return;
        if (stripeDeleteResult.data) {
            setStripe({ configured: false });
            setStripePk('');
            setStripeSk('');
            setStripeEditing(false);
            showSuccess('Stripe disconnected');
        }
        if (stripeDeleteResult.error) showError(typeof stripeDeleteResult.error === 'string' ? stripeDeleteResult.error : 'Failed to disconnect Stripe');
        setStripeDisconnecting(false);
    }, [stripeDeleteResult.data, stripeDeleteResult.error]);

    const isStripeTerminalLog = (description: string) =>
        /sync complete|sync failed/i.test(description);

    const fetchStripeSyncStatus = useCallback(() => {
        if (!entityIdRef.current) return;
        doGetStripeSyncStatus(buildApiRequestConfig(routes.stripe.syncStatus, [{ param: 'entityId', value: entityIdRef.current }]));
    }, [doGetStripeSyncStatus]);

    const startStripeSyncPolling = useCallback(() => {
        if (stripeSyncPollRef.current) clearInterval(stripeSyncPollRef.current);
        fetchStripeSyncStatus(); // fetch immediately
        stripeSyncPollRef.current = setInterval(fetchStripeSyncStatus, 3000);
    }, [fetchStripeSyncStatus]);

    // Stop polling on unmount
    useEffect(() => {
        return () => { if (stripeSyncPollRef.current) clearInterval(stripeSyncPollRef.current); };
    }, []);

    // Stripe sync trigger result — just start polling once the request lands
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!stripeSyncResult.data && !stripeSyncResult.error) return;
        if (stripeSyncResult.error) {
            showError(typeof stripeSyncResult.error === 'string' ? stripeSyncResult.error : 'Failed to start Stripe sync');
            setStripeSyncing(false);
        } else {
            startStripeSyncPolling();
        }
    }, [stripeSyncResult.data, stripeSyncResult.error]);

    // Stripe sync status polling result
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!stripeSyncStatusResult.data) return;
        const logs: any[] = stripeSyncStatusResult.data.data ?? stripeSyncStatusResult.data ?? [];
        if (logs.length === 0) return;
        const latest = logs[0];
        const description: string = latest.description ?? '';
        setStripeSyncText(description);
        if (isStripeTerminalLog(description)) {
            if (stripeSyncPollRef.current) { clearInterval(stripeSyncPollRef.current); stripeSyncPollRef.current = null; }
            setStripeSyncing(false);
            if (/sync complete/i.test(description)) {
                showSuccess(description);
                // Refresh stripe info so lastSync updates
                if (entityIdRef.current) doGetStripe(buildApiRequestConfig(routes.stripe.get, [{ param: 'entityId', value: entityIdRef.current }]));
            } else {
                showError(description);
            }
        }
    }, [stripeSyncStatusResult.data]);

    const handleStripeSync = () => {
        if (!hasStripe) { promptStripe(); return; }
        if (!entityId) return;
        setStripeSyncing(true);
        setStripeSyncText('Starting sync...');
        doSyncStripe(buildApiRequestConfig(routes.stripe.sync, [{ param: 'entityId', value: entityId }]));
    };

    const handleStripeSave = () => {
        if (!hasStripe) { promptStripe(); return; }
        if (!entityId || !stripePk.trim() || !stripeSk.trim()) return;
        setStripeSaving(true);
        doSaveStripe({
            ...buildApiRequestConfig(routes.stripe.save, [{ param: 'entityId', value: entityId }]),
            body: { publishableKey: stripePk.trim(), secretKey: stripeSk.trim() },
        });
    };

    const handleStripeDisconnect = () => {
        if (!entityId) return;
        setStripeDisconnecting(true);
        doDeleteStripe(buildApiRequestConfig(routes.stripe.remove, [{ param: 'entityId', value: entityId }]));
    };

    const isConnected = integration?.status === 'Active';
    const isPending = integration?.status === 'Pending';

    return (
        <div style={{ width: '100%' }}>
            {invoiceHorizonDialog}
            <Topbar title="Integrations"/>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionWrapper title="Xero">
                    {!hasXero && <PlanUpgradeBar onUpgrade={promptXero} sx={{ mt: 2, mb: 1 }} />}
                    {isConnected
                        ? <XeroConnectedView
                            organisationName={integration?.tenantName}
                            lastGLSyncAt={lastGLSyncAt}
                            lastTransactionSyncAt={lastTransactionSyncAt}
                            lastStatusesSyncAt={lastStatusesSyncAt}
                            lastInvoicesSyncAt={lastInvoicesSyncAt}
                            lastClientsSyncAt={lastClientsSyncAt}
                            syncResults={syncResults}
                            importStatusText={importStatusText}
                            onDisconnect={handleDisconnect}
                            onSyncGL={handleSyncGL}
                            onSyncTransactions={handleSyncTransactions}
                            onSyncStatuses={handleSyncStatuses}
                            onSyncInvoices={handleSyncInvoices}
                            onSyncClients={handleSyncClients}
                            syncingGL={syncingGL}
                            syncingTransactions={syncingTransactions}
                            syncingStatuses={syncingStatuses}
                            syncingInvoices={syncingInvoices}
                            syncingClients={syncingClients}
                            statusesSynced={statusesSynced}
                            bankAccounts={bankAccounts}
                            selectedBankAccountId={selectedBankAccountId}
                            onBankAccountChange={setSelectedBankAccountId}
                            xeroBankAccounts={xeroBankAccounts}
                            selectedXeroBankAccountId={selectedXeroBankAccountId}
                            onXeroBankAccountChange={setSelectedXeroBankAccountId}
                            fromDate={fromDate}
                            onFromDateChange={setFromDate}
                            canSync={can('integrations.sync', 'update')}
                            canConnect={can('integrations.connect', 'update')}
                          />
                        : <XeroConnectView
                            onConnect={handleConnect}
                            connecting={connecting}
                            pending={isPending}
                            canConnect={can('integrations.connect', 'update')}
                          />}
                </SectionWrapper>
                <div ref={stripeSectionRef}>
                <SectionWrapper title="Stripe">
                    {!hasStripe && <PlanUpgradeBar onUpgrade={promptStripe} sx={{ mt: 2, mb: 1 }} />}
                    {stripe?.configured && !stripeEditing ? (
                        <StripeConnectedView
                            publishableKey={stripe.publishableKey}
                            lastSync={stripe.lastSync}
                            syncText={stripeSyncText}
                            syncing={stripeSyncing}
                            onSync={handleStripeSync}
                            onUpdateKeys={() => (hasStripe ? setStripeEditing(true) : promptStripe())}
                            onDisconnect={handleStripeDisconnect}
                            canSync={can('integrations.sync', 'update')}
                            canConnect={can('integrations.connect', 'update')}
                        />
                    ) : (
                        // Read-only without the feature - the key fields open the prompt.
                        <Box
                            onFocusCapture={(e) => {
                                if (hasStripe || !(e.target instanceof HTMLInputElement)) return;
                                e.target.blur();
                                promptStripe();
                            }}
                        >
                        <StripeConnectView
                            publishableKey={stripePk}
                            secretKey={stripeSk}
                            onPkChange={setStripePk}
                            onSkChange={setStripeSk}
                            onSave={handleStripeSave}
                            onCancel={stripeEditing ? () => { setStripeEditing(false); setStripePk(''); setStripeSk(''); } : undefined}
                            saving={stripeSaving}
                            isUpdate={stripeEditing}
                            canConnect={can('integrations.connect', 'update')}
                        />
                        </Box>
                    )}
                </SectionWrapper>
                </div>
                <SectionWrapper title="QuickBooks">
                    orem ipsum dolor sit amet, consectetur adipiscing elit. Donec vel sapien eget nunc gravida efficitur. Sed at felis a enim efficitur commodo. Curabitur ac ligula id metus efficitur convallis. Donec in odio sed nisl convallis tincidunt. Nulla facilisi. Donec ut velit a nunc efficitur fermentum. Suspendisse potenti.
                </SectionWrapper>
                <SectionWrapper title="Other Integrations">
                    orem ipsum dolor sit amet, consectetur adipiscing elit. Donec vel sapien eget nunc gravida efficitur. Sed at felis a enim efficitur commodo. Curabitur ac ligula id metus efficitur convallis. Donec in odio sed nisl convallis tincidunt. Nulla facilisi. Donec ut velit a nunc efficitur fermentum. Suspendisse potenti.
                </SectionWrapper>
            </div>
        </div>
    );
};

export default Integrations;
